import {
  AddressesApi,
  ArbitrationsApi,
  AuthApi,
  BankAccountsApi,
  BlockedTenantsApi,
  ChecksApi,
  ClaimDocumentsApi,
  ClaimItemCorrectionsApi,
  ClaimItemDocumentsApi,
  ClaimItemsApi,
  ClaimStripePaymentsApi,
  ClaimsApi,
  CommissionApi,
  ConcurrentTenancyCheckApi,
  DocumentsApi,
  EvidenceChasersApi,
  IntegratorApi,
  IntegratorCommissionApi,
  InternalReportingApi,
  InternalTeamMembersApi,
  KpiDocumentApi,
  LandlordsApi,
  OrderCustomerActionsApi,
  OrderManualPaymentsApi,
  OrdersApi,
  OrganizationBankAccountsApi,
  OrganizationBrandingApi,
  OrganizationGroupsApi,
  OrganizationInternalOwnersApi,
  OrganizationInvitesApi,
  OrganizationsApi,
  OrganizationUsersApi,
  PaymentPlanInstalmentsApi,
  PaymentPlansApi,
  ReconciliationDocumentsApi,
  ReferenceDocumentsApi,
  ReferencesApi,
  TenanciesApi,
  TenancyAnniversariesApi,
  TenancyCheckoutsApi,
  TenancyExpiriesApi,
  TenancyInvitesApi,
  TenancyOrderNotesApi,
  TenancyOrdersApi,
  TenancyUsersApi,
  TenantsApi,
  UsersApi,
} from '@reposit/api-client';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { Dispatch } from 'redux';
import { call, getContext, put, select } from 'redux-saga/effects';
import { AppDispatch } from '../..';
import { appConfig } from '../../appConfig';
import { logout } from '../auth/auth.actions';
import { getAccessToken } from '../auth/auth.selectors';
import { restoreAccessToken } from '../auth/internal-session';
import { AppState } from '../root.reducer';

const HTTP_UNAUTHORIZED = 401;

export class OrganizationAccountManagersApiCompat {
  constructor(private axiosInstance: AxiosInstance) {}

  assignAccountManager(organizationId: string, assignAccountManagerDTO: { internalTeamMemberId: string }) {
    return this.axiosInstance.put(`/v2/organizations/${organizationId}/account-manager`, assignAccountManagerDTO);
  }

  unassignAccountManager(organizationId: string) {
    return this.axiosInstance.delete(`/v2/organizations/${organizationId}/account-manager`);
  }
}

export const runSagaWithAuth = (saga: () => any) =>
  function* () {
    try {
      const result = yield call(() => saga());
      return result;
    } catch (e) {
      if (e && e.response && e.response.status === HTTP_UNAUTHORIZED) {
        yield put(logout());
      }
      throw e;
    }
  };

export const runThunkWithAuth = (thunk: () => Promise<any>, dispatch: AppDispatch) => {
  return thunk().catch((e) => {
    if (e && e.response && e.response.status === HTTP_UNAUTHORIZED) {
      dispatch(logout());
    }
    throw e;
  });
};

// An admin's token lasts 15 minutes, so a 401 restores a new one from the session cookie and retries the call once.
// The retry goes through plain axios, so a second 401, like a refused restore, reaches runSagaWithAuth or
// runThunkWithAuth, which sign the admin out. A restore tenancy couldn't check fails the call with its 5xx instead,
// which leaves the admin signed in to try again.
function createSessionRestoringAxiosInstance(accessToken: string, dispatch: Dispatch): AxiosInstance {
  const instance = axios.create({
    baseURL: appConfig.api.basePath,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    if (!error.config || !error.response || error.response.status !== HTTP_UNAUTHORIZED) {
      throw error;
    }
    const restoredToken = await restoreAccessToken(dispatch);
    if (!restoredToken) {
      throw error;
    }
    error.config.headers.Authorization = `Bearer ${restoredToken}`;
    return axios.request(error.config);
  });
  return instance;
}

export function* createAxiosInstance() {
  const accessToken = yield select(getAccessToken);
  // index.tsx puts the store's dispatch in the saga context.
  const dispatch: Dispatch = yield getContext('dispatch');
  return createSessionRestoringAxiosInstance(accessToken, dispatch);
}

export function createStandardAxiosInstance(state: AppState, dispatch: Dispatch) {
  return createSessionRestoringAxiosInstance(getAccessToken(state), dispatch);
}

export function* createTenanciesApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenanciesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createConcurrentTenancyCheckApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ConcurrentTenancyCheckApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenantsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenantsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createClaimsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ClaimsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createClaimItemsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ClaimItemsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createClaimItemCorrectionsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ClaimItemCorrectionsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createClaimItemDocumentsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ClaimItemDocumentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createClaimDocumentsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ClaimDocumentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createUsersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new UsersApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createBlockedTenantsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new BlockedTenantsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyOrdersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyOrdersApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyAnniversaryApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyAnniversariesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createBankAccountsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new BankAccountsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationBankAccountsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationBankAccountsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationGroupsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationGroupsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createAddressesApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new AddressesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyCheckoutsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyCheckoutsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationInviteApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationInvitesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationUserApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationUsersApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyInviteApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyInvitesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyExpiriesApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyExpiriesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createDocumentsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new DocumentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createLandlordsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new LandlordsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createReferencesApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ReferencesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrderCustomerActionApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrderCustomerActionsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createReferenceDocumentsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ReferenceDocumentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createChecksApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ChecksApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createIntegratorsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new IntegratorApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationInternalOwnersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationInternalOwnersApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationBrandingApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationBrandingApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createInternalTeamMembersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new InternalTeamMembersApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createOrganizationAccountManagersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new OrganizationAccountManagersApiCompat(axiosInstance);
}

export function* createIntegratorCommissionApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new IntegratorCommissionApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createCommissionApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new CommissionApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createArbitrationsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ArbitrationsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createKpiDocumentsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new KpiDocumentApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createReconciliationDocumentsApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new ReconciliationDocumentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createInternalReportingApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new InternalReportingApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyOrderNotesApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyOrderNotesApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createAuthApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new AuthApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createTenancyUsersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new TenancyUsersApi({}, appConfig.api.basePath, axiosInstance);
}

export function* createEvidenceChasersApi() {
  const axiosInstance: AxiosInstance = yield createAxiosInstance();
  return new EvidenceChasersApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardClaimsApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new ClaimsApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardEvidenceChasersApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new EvidenceChasersApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardUsersApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new UsersApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardPaymentPlansApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new PaymentPlansApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardPaymentPlanInstalmentsApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new PaymentPlanInstalmentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardOrdersApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new OrdersApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardOrganizationGroupsApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new OrganizationGroupsApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardOrderManualPaymentsApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new OrderManualPaymentsApi({}, appConfig.api.basePath, axiosInstance);
}

export function createStandardClaimStripePaymentsApi(state: AppState, dispatch: Dispatch) {
  const axiosInstance: AxiosInstance = createStandardAxiosInstance(state, dispatch);
  return new ClaimStripePaymentsApi({}, appConfig.api.basePath, axiosInstance);
}
